import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clinic, galleryImages } from '../data/site.js';

export default function GalleryPage() {
  const [activeIndex, setActiveIndex] = useState(null);
  const activeImage = activeIndex === null ? null : galleryImages[activeIndex];

  function showPreviousImage() {
    setActiveIndex((index) => {
      if (index === null) return null;
      return index === 0 ? galleryImages.length - 1 : index - 1;
    });
  }

  function showNextImage() {
    setActiveIndex((index) => {
      if (index === null) return null;
      return (index + 1) % galleryImages.length;
    });
  }

  useEffect(() => {
    if (activeIndex === null) return undefined;

    function onKeyDown(event) {
      if (event.key === 'Escape') setActiveIndex(null);
      if (event.key === 'ArrowLeft') {
        setActiveIndex((index) => {
          if (index === null) return null;
          return index === 0 ? galleryImages.length - 1 : index - 1;
        });
      }
      if (event.key === 'ArrowRight') {
        setActiveIndex((index) => {
          if (index === null) return null;
          return (index + 1) % galleryImages.length;
        });
      }
    }

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [activeIndex]);

  return (
    <>
      <section className="gallery-hero">
        <div className="container">
          <div className="gallery-hero__content">
            <p className="caption">Clinic gallery</p>
            <h1 className="heading-display">Dental clinic gallery in Varanasi</h1>
            <p className="text-body">
              See the clinic before you visit, including the entrance, reception area, treatment room and clinic-approved dental case photos from {clinic.name} in {clinic.locality}.
            </p>
            <Link to="/#booking" className="btn btn--primary">Book an appointment</Link>
          </div>
        </div>
      </section>

      <section className="gallery-page" aria-labelledby="gallery-title">
        <div className="container">
          <div className="gallery-page__header">
            <div>
              <p className="caption">Photo gallery</p>
              <h2 className="heading-2" id="gallery-title">Clinic gallery</h2>
            </div>
            <p className="text-body">
              These photos help new patients understand the clinic environment and feel more prepared before booking an appointment.
            </p>
          </div>

          <div className="gallery-page__intro">
            <article>
              <h3>Clinic photos</h3>
              <p>The exterior photo helps patients identify the clinic when using Google Maps or arriving for the first time. Reception and treatment-room photos show the appointment setting so patients know what to expect before they visit.</p>
            </article>
            <article>
              <h3>Dental case gallery</h3>
              <p>Case photos are shown only where appropriate and should be understood as examples, not guarantees. Every patient has different teeth, gums, bite, oral health, medical history and treatment goals.</p>
            </article>
            <article>
              <h3>Before you compare results</h3>
              <p>A photo cannot show the full clinical situation. Use the gallery as a starting point and ask the dentist what is realistic after your own assessment.</p>
            </article>
          </div>

          <div className="gallery-grid">
            {galleryImages.map((image, index) => (
              <button
                type="button"
                className="gallery-card"
                key={image.src}
                onClick={() => setActiveIndex(index)}
                aria-label={`Open ${image.title}`}
              >
                <img src={image.src} alt={image.alt} loading={index < 4 ? 'eager' : 'lazy'} />
                <span>{image.title}</span>
              </button>
            ))}
          </div>

          <div className="gallery-page__cta">
            <p>If a gallery photo is similar to something you want help with, book an appointment and mention it during your consultation.</p>
            <Link to="/#booking" className="btn btn--primary">Book a visit</Link>
          </div>
        </div>
      </section>

      {activeImage ? (
        <div className="gallery-lightbox" role="dialog" aria-modal="true" aria-label={activeImage.title}>
          <button className="gallery-lightbox__backdrop" type="button" aria-label="Close gallery overlay" onClick={() => setActiveIndex(null)} />
          <div className="gallery-lightbox__panel">
            <button className="gallery-lightbox__close" type="button" onClick={() => setActiveIndex(null)} aria-label="Close gallery image">
              x
            </button>
            <button className="gallery-lightbox__nav gallery-lightbox__nav--prev" type="button" onClick={showPreviousImage} aria-label="Previous gallery image">
              &lt;
            </button>
            <img src={activeImage.src} alt={activeImage.alt} />
            <button className="gallery-lightbox__nav gallery-lightbox__nav--next" type="button" onClick={showNextImage} aria-label="Next gallery image">
              &gt;
            </button>
            <p>{activeImage.title}</p>
          </div>
        </div>
      ) : null}
    </>
  );
}
